import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Sparkles, Clock, Lock, CheckCircle2, Gift, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatDT } from "@/lib/format";
import { useTrialCountdown } from "@/lib/useTrialCountdown";

// Settings → Subscription card. Separate component so Settings.jsx (already
// ~1500 lines) doesn't balloon any further. It fetches /api/subscription/me
// on mount and renders one of five shapes:
//   • Admin          — "Admin access — full features" badge, no billing info
//   • Complimentary  — "Gifted premium" with expiry or "lifetime"
//   • Paid           — active / cancelled subscription with billing dates
//   • Trial          — days remaining + Upgrade CTA
//   • Free           — read-only notice + Upgrade CTA
export const SubscriptionCard = () => {
  const [sub, setSub] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/subscription/me");
        if (!cancelled) setSub(data);
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || "Couldn't load subscription");
      }
    };
    load();

    // Live refresh while the user waits for their 3rd payment to land. We
    // only poll in the narrow window where it matters (paid plan, fewer than
    // 3 successful payments, tab visible) so we never hammer the API for
    // the 98% of users who are on trial/free/complimentary/admin or already
    // past the minimum commitment.
    let timer = null;
    const maybePoll = () => {
      const latest = sub;
      const plan = latest?.plan?.plan;
      const paymentsCount = latest?.payments_count ?? 0;
      const shouldPoll = plan === "paid" && paymentsCount < 3 && !document.hidden;
      if (shouldPoll && !timer) {
        timer = setInterval(load, 30000);
      } else if (!shouldPoll && timer) {
        clearInterval(timer);
        timer = null;
      }
    };
    maybePoll();
    document.addEventListener("visibilitychange", maybePoll);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", maybePoll);
    };
    // Deliberately only re-run polling logic when the relevant fields on sub
    // change, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sub?.plan?.plan, sub?.payments_count]);

  // v77.5 — Live trial countdown. MUST be declared BEFORE any early
  // returns so React hook order stays stable across renders.
  const trialRemaining = useTrialCountdown(
    sub?.plan?.plan === "trial" ? sub?.plan?.trial_end_at : null,
  );

  if (err) {
    return (
      <section className="border border-[#D9E1E3] bg-white rounded-md p-6" data-testid="subscription-card-error">
        <h2 className="font-heading text-lg font-medium">Subscription</h2>
        <p className="text-sm text-[#8C2A22] mt-2">{String(err)}</p>
      </section>
    );
  }
  if (!sub) {
    return (
      <section className="border border-[#D9E1E3] bg-white rounded-md p-6" data-testid="subscription-card-loading">
        <h2 className="font-heading text-lg font-medium">Subscription</h2>
        <p className="text-sm text-[#6F828A] mt-2">Loading…</p>
      </section>
    );
  }

  const plan = sub.plan || {};
  const pill = (() => {
    switch (plan.plan) {
      case "admin":         return { bg: "bg-[#D6F0EF]", fg: "text-[#116B6E]", border: "border-[#A7D8DA]", label: "Admin", Icon: ShieldCheck };
      case "complimentary": return { bg: "bg-[#ECE3F3]", fg: "text-[#4E3877]", border: "border-[#C5B2DE]", label: "Complimentary", Icon: Gift };
      case "paid":          return { bg: "bg-[#DCEFDC]", fg: "text-[#1E5A1E]", border: "border-[#A9D3A9]", label: "Paid", Icon: CheckCircle2 };
      case "trial":         return { bg: "bg-[#FDF4E2]", fg: "text-[#8F6B1F]", border: "border-[#E6CFA2]", label: "Trial", Icon: Clock };
      default:              return { bg: "bg-[#F0EFEE]", fg: "text-[#6F828A]", border: "border-[#CFD6D8]", label: "Free",  Icon: Lock };
    }
  })();
  const Icon = pill.Icon;

  return (
    <section className="border border-[#D9E1E3] bg-white rounded-md p-6 space-y-4" data-testid="subscription-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-heading text-lg font-medium">Subscription</h2>
            {/* v76.6 — Make it unmistakable that billing is per parent
                account, not per child. The same badge + copy renders
                in every child context (including "All children") so
                switching the global ChildSwitcher never makes it look
                like billing is happening per-child. */}
            <span
              className="text-[10px] uppercase tracking-[0.1em] text-[#116B6E] bg-[#E0F2F2] border border-[#A7D8DA] rounded-sm px-1.5 py-0.5"
              data-testid="subscription-account-wide-badge"
            >
              Account-wide
            </span>
          </div>
          <p className="text-sm text-[#2E3A3F]">
            Your subscription applies to your parent account and includes all children linked to your profile. You will not be charged separately per child.
          </p>
          <p className="text-xs text-[#6F828A]" data-testid="subscription-price-line">
            {sub.plan_interval === "yearly"
              ? `€${(sub.price_yearly_eur ?? sub.price_eur ?? 99).toFixed(0)} per year, billed upfront. Cancel anytime — access stays until renewal.`
              : `€${(sub.price_monthly_eur ?? sub.price_eur ?? 10.99).toFixed(2)} per month. Minimum 3 monthly payments required.`}
          </p>
        </div>
        <span
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border text-xs uppercase tracking-[0.1em] ${pill.bg} ${pill.fg} ${pill.border}`}
          data-testid={`subscription-pill-${plan.plan}`}
        >
          <Icon className="w-3.5 h-3.5" strokeWidth={1.75} /> {pill.label}
        </span>
      </div>

      {plan.plan === "trial" && (
        <div className="text-sm text-[#2E3A3F] border-t border-[#EEF3F4] pt-4">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <span>Trial remaining</span>
            <span className="font-mono-data tabular-nums text-[#2E3A3F] text-lg" data-testid="subscription-trial-remaining">{trialRemaining || "—"}</span>
          </div>
          {plan.trial_end_at && (
            <div className="flex items-baseline justify-between flex-wrap gap-2 mt-1 text-xs text-[#6F828A]">
              <span>Ends</span>
              <span className="font-mono-data">{formatDT(plan.trial_end_at)}</span>
            </div>
          )}
          <p className="text-xs text-[#6F828A] mt-2">When your trial ends, you&apos;ll have a 7-day read-only grace period — sign in, view every record, review Insights and browse Court Bundles. After that, your account is locked until you subscribe; sign-in still works and your data is preserved indefinitely.</p>
        </div>
      )}

      {plan.plan === "free" && (
        <div className="text-sm text-[#8F6B1F] bg-[#FDF4E2] border-t border-[#E6CFA2] -mx-6 px-6 pt-4 mt-2">
          Your trial has ended — you&apos;re in a <strong>7-day read-only grace period</strong>. You can still sign in, view every record, review Insights and browse Court Bundles. Exports, downloads, Court Bundle generation and external shares are paid features. Subscribe before the grace period ends to keep adding new entries.
        </div>
      )}

      {plan.plan === "locked" && (
        <div className="text-sm text-[#8C2A22] bg-[#FBEFED] border-t border-[#E8B8B2] -mx-6 px-6 pt-4 mt-2">
          Your account is <strong>locked</strong>. Sign-in still works and you can view every record — subscribe whenever you&apos;re ready to start recording again. Your data is preserved indefinitely and nothing is deleted.
        </div>
      )}

      {plan.plan === "paid" && (
        <div className="text-sm text-[#2E3A3F] border-t border-[#EEF3F4] pt-4 space-y-1.5">
          <div className="flex items-baseline justify-between" data-testid="subscription-plan-interval">
            <span className="text-[#6F828A]">Billing cadence</span>
            <span className="font-medium capitalize">{sub.plan_interval || "monthly"}</span>
          </div>
          {/* Show payment-count progress for monthly plans only — yearly
              has no payment minimum. */}
          {typeof sub.payments_count === "number" && (sub.plan_interval || "monthly") !== "yearly" && (
            <div className="flex items-baseline justify-between" data-testid="subscription-payments-count">
              <span className="text-[#6F828A]">Successful payments</span>
              <span className="font-mono-data tabular-nums">{sub.payments_count} / {sub.payments_required_for_cancel || 3} minimum</span>
            </div>
          )}
          {sub.next_billing_date && (
            <div className="flex items-baseline justify-between">
              <span className="text-[#6F828A]">Next billing date</span>
              <span className="font-mono-data">{formatDT(sub.next_billing_date)}</span>
            </div>
          )}
          {sub.subscription_status === "cancelled" && sub.access_until && (
            <div className="flex items-baseline justify-between">
              <span className="text-[#6F828A]">Access continues until</span>
              <span className="font-mono-data">{formatDT(sub.access_until)}</span>
            </div>
          )}
        </div>
      )}

      {plan.plan === "complimentary" && (
        <div className="text-sm text-[#2E3A3F] border-t border-[#EEF3F4] pt-4">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <span className="text-[#6F828A]">Access</span>
            <span className="font-mono-data">{sub.complimentary?.expires_at ? `until ${formatDT(sub.complimentary.expires_at)}` : "Lifetime"}</span>
          </div>
          {sub.complimentary?.reason && (
            <div className="flex items-baseline justify-between mt-1">
              <span className="text-[#6F828A]">Reason</span>
              <span>{sub.complimentary.reason}</span>
            </div>
          )}
        </div>
      )}

      {plan.plan === "admin" && (
        <p className="text-sm text-[#116B6E] border-t border-[#EEF3F4] pt-4">
          You&apos;re signed in as an admin. Full access is always granted regardless of subscription state.
        </p>
      )}

      <div className="flex flex-col items-start gap-2 pt-2">
        {plan.plan !== "admin" && plan.plan !== "complimentary" && plan.plan !== "paid" && (
          <Link
            to="/app/upgrade"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-white bg-[#1F9FA4] hover:bg-[#178085] rounded-sm px-4 py-2"
            data-testid="subscription-upgrade-cta"
          >
            <Sparkles className="w-4 h-4" strokeWidth={1.75} /> Upgrade to Paid
          </Link>
        )}
        {plan.plan === "paid" && sub.subscription_status === "active" && (
          (() => {
            // v77.99.10 — Yearly subscribers can cancel any time;
            // monthly users still hit the 3-payment gate.
            const isYearly = (sub.plan_interval || "monthly") === "yearly";
            const required = sub.payments_required_for_cancel || (isYearly ? 1 : 3);
            const canCancel = isYearly || (sub.payments_count ?? 0) >= required;
            return (
              <>
                <Button
                  type="button"
                  variant="outline"
                  disabled={!canCancel}
                  className="rounded-sm"
                  data-testid="subscription-cancel-cta"
                  title={canCancel ? "" : "Available after your 3rd payment"}
                  onClick={async () => {
                    if (!canCancel) return;
                    const confirmMsg = isYearly
                      ? "Cancel your subscription? Your paid access continues until the end of your current annual billing period. After that, your account moves into a 7-day read-only grace period and is then locked until you reactivate. Your data stays on file."
                      : "Cancel your subscription? Your paid access continues until the end of your current billing period. After that, your account moves into a 7-day read-only grace period (sign-in, view, export and re-subscribe), and is then locked until you reactivate. Your data stays on file.";
                    if (!window.confirm(confirmMsg)) return;
                    try {
                      const { data } = await api.post("/subscription/cancel");
                      // Swap in the freshly returned state
                      setSub((prev) => prev ? {
                        ...prev,
                        subscription_status: "cancelled",
                        access_until: data.access_until,
                        plan: { ...(prev.plan || {}), subscription_status: "cancelled", access_until: data.access_until },
                      } : prev);
                    } catch (e) {
                      const d = e.response?.data?.detail;
                      const msg = typeof d === "string" ? d : (d?.message || "Couldn't cancel");
                      window.alert(msg);
                    }
                  }}
                >
                  {canCancel ? "Cancel subscription" : `Cancel available after your ${required === 3 ? "3rd" : `${required}th`} payment`}
                </Button>
                <p className="text-xs text-[#6F828A]" data-testid="subscription-cancel-help">
                  {canCancel
                    ? (isYearly
                      ? "You can cancel anytime. Your access will continue until the end of your annual billing period."
                      : "You can cancel anytime. Your access will continue until the end of your current billing period.")
                    : `${Math.max(0, required - (sub.payments_count ?? 0))} more payment${Math.max(0, required - (sub.payments_count ?? 0)) === 1 ? "" : "s"} until cancellation is unlocked.`}
                </p>
              </>
            );
          })()
        )}
        {plan.plan === "paid" && sub.subscription_status === "cancelled" && (
          <p className="text-xs text-[#6F828A]">
            Your subscription has been cancelled. You&apos;ll stay on the Paid plan until {sub.access_until ? formatDT(sub.access_until) : "the end of the current billing period"}.
          </p>
        )}
      </div>
    </section>
  );
};
